<template>
  <div
    v-for="def in CHANNELS"
    :key="def.label"
    class="flex min-w-0 grow flex-col items-center gap-1"
  >
    <input
      v-model="draft[def.id]"
      class="w-full rounded-md border border-border bg-transparent px-2.5 py-1.5 text-center text-sm focus:outline focus:outline-2 focus:-outline-offset-1 focus:outline-blue-500"
      inputmode="numeric"
      pattern="[0-9]*"
      @blur="reset()"
    />
    <span class="sr-only">{{ def.label }}</span>
  </div>
</template>

<script setup lang="ts">
import {
  type Hsl,
  type Lch,
  convertHslToRgb,
  convertLabToLch,
  convertLabToRgb,
  convertLchToLab,
  convertRgbToHsl,
  convertRgbToLab,
} from "culori/fn";
import isDeepEqual from "fast-deep-equal";
import { ref, watch } from "vue";

const CHANNELS = [
  { id: "l", label: "L" },
  { id: "c", label: "C" },
  { id: "h", label: "H" },
] as const;

const modelValue = defineModel<Hsl>({ required: true });

let snapshot: Lch | undefined;
const draft = ref({ l: "0", c: "0", h: "0" });

const reset = () => {
  if (!snapshot) {
    return;
  }

  draft.value = {
    l: "" + Math.round(snapshot.l),
    c: "" + Math.round(snapshot.c),
    h: "" + Math.round(snapshot.h ?? 0),
  };
};

function quantize(lch: Lch): Lch {
  return {
    mode: "lch",
    l: Math.round(lch.l),
    c: Math.round(lch.c),
    h: Math.round(lch.h ?? 0),
  };
}

watch(
  modelValue,
  (hsl) => {
    const lch = quantize(
      convertLabToLch(convertRgbToLab(convertHslToRgb(hsl)), "lch")
    );
    if (isDeepEqual(lch, snapshot)) {
      return;
    }

    snapshot = lch;
    reset();
  },
  { immediate: true }
);

const parseChannel = (raw: string, min: number, max: number): number => {
  let num = Number(raw);
  if (!Number.isFinite(num)) {
    num = 0;
  }

  return Math.max(min, Math.min(num, max));
};

watch(
  draft,
  (draft) => {
    const l = parseChannel(draft.l, 0, 100);
    const c = parseChannel(draft.c, 0, 150);
    const h = parseChannel(draft.h, 0, 360);

    const next: Lch = { mode: "lch", l, c, h };
    if (isDeepEqual(next, snapshot)) {
      return;
    }

    snapshot = next;
    modelValue.value = convertRgbToHsl(convertLabToRgb(convertLchToLab(next)));
  },
  { deep: true }
);
</script>
